import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  Button,
  InputLabel,
  FormHelperText,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AddBoxIcon from '@material-ui/icons/AddBox';
import WarningIcon from '@material-ui/icons/Warning';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import LayersIcon from '@material-ui/icons/Layers';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import {
  formattedCurrency,
  calculateMarginByValue,
  calculateProfitByValue,
  calculateValueByMargin,
  capitalize,
  safeParseParticulars,
} from '../../../utils/utils';
import ProductUnitSelect from '../Common/ProductUnitSelect';
import { normalizeUnitCode } from '../../../constants/productUnits';

const useStyles = makeStyles((theme) => ({
  rootRow: {
    '& > *': { borderBottom: 'unset' },
    transition: 'background-color 0.2s ease',
  },
  expandedRow: {
    backgroundColor: '#f8fafc',
  },
  attentionRow: {
    backgroundColor: '#fffdf5',
  },
  expandedBox: {
    padding: theme.spacing(2, 3),
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    margin: theme.spacing(1, 0, 1.5, 0),
    border: '1px solid #cbd5e1',
  },
  formatChip: {
    backgroundColor: '#ffffff',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 4,
  },
  modeSegmented: {
    display: 'flex',
    borderRadius: 6,
    border: '1px solid #cbd5e1',
    overflow: 'hidden',
    marginTop: 4,
  },
  modeBtn: {
    flex: 1,
    padding: '6px 10px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: '#ffffff',
    color: '#475569',
    fontWeight: 500,
    outline: 'none',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
    '&.active': {
      backgroundColor: '#17497F',
      color: '#ffffff',
      fontWeight: 600,
    },
  },
}));

const ImportRowDesktop = ({
  product,
  index,
  resolution = {},
  isSelected,
  onToggleSelect,
  onRemove,
  onChangeResolution,
  categoryList = [],
  onOpenFormatModal,
}) => {
  const classes = useStyles();
  const [openExpand, setOpenExpand] = useState(false);

  const mode = resolution.conversionMode || (product.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
  const itemsPerUnit = resolution.itemsPerUnit || product.suggestedItemsPerUnit || 1;
  const currentUnit = normalizeUnitCode(resolution.unit || (mode === 'FRACTION_ONLY' ? 'UN' : (product.unit || 'UN')));

  // Cálculos de quantidade e custos
  let displayAmount = product.amount;
  let unitCost = product.cost;

  if (mode === 'FRACTION_ONLY') {
    displayAmount = product.amount * itemsPerUnit;
    unitCost = product.cost / itemsPerUnit;
  }

  const saleValue = resolution.value !== undefined ? resolution.value : calculateValueByMargin(unitCost, resolution.margin || 30);
  const margin = resolution.margin !== undefined ? resolution.margin : calculateMarginByValue(unitCost, saleValue);
  const profit = calculateProfitByValue(unitCost, saleValue);

  const childFormats = resolution.childFormats || [];

  const isExisting = product.analysis?.status === 'EXISTING';
  const isAdoptedAsChild = mode === 'PARENT_CHILD' && childFormats.some(
    (f) => String(f.id) === String(product.analysis?.product?.id) || f.wasAdoptedAsChild,
  );
  const currentAction = isAdoptedAsChild
    ? 'NEW'
    : (resolution.action || (isExisting ? 'ADD' : 'NEW'));
  const isAddAction = currentAction === 'ADD';

  // Preservação da categoria para produto existente (ação ADD)
  const preservedCategoryId = isExisting
    ? (product.analysis?.product?.category || resolution.category)
    : resolution.category;
  const currentCategoryId = isAddAction ? preservedCategoryId : resolution.category;
  const selectedCategory = categoryList.find((c) => c.id === currentCategoryId) || categoryList[0] || null;

  // Particularidades da categoria para produto novo
  const categoryParticularsDef = !isAddAction ? safeParseParticulars(selectedCategory?.particulars) : {};
  const hasParticulars = Object.keys(categoryParticularsDef).length > 0;

  const handleModeChange = (newMode) => {
    let updates = { conversionMode: newMode };
    const isExisting = product.analysis?.status === 'EXISTING';
    const existing = isExisting ? product.analysis.product : null;
    const existingHasFormats = existing?.childFormats && existing.childFormats.length > 0;

    if (newMode === 'FRACTION_ONLY') {
      const factor = itemsPerUnit > 1 ? itemsPerUnit : (product.suggestedItemsPerUnit || 12);
      updates.itemsPerUnit = factor;
      const newUnitCost = product.cost / factor;
      updates.value = isExisting && existing?.value ? existing.value : calculateValueByMargin(newUnitCost, resolution.margin || 30);
      updates.childFormats = [];
      if (isExisting && !existingHasFormats) {
        updates.action = 'ADD';
        updates.productId = existing.id;
        updates.customName = existing.name || product.name;
      }
    } else if (newMode === 'PARENT_CHILD') {
      updates.itemsPerUnit = 1;
      const factor = itemsPerUnit > 1 ? itemsPerUnit : (product.suggestedItemsPerUnit || 1);

      if (isExisting && existingHasFormats) {
        // O produto existente já era um Pai (Caixa com formatos cadastrados)
        updates.action = 'ADD';
        updates.productId = existing.id;
        updates.value = existing.value;
      } else if (isExisting && !existingHasFormats) {
        // O produto existente era a UNIDADE avulsa!
        // A Caixa da NFe entra como novo Pai (action = 'NEW')
        // E o produto existente é adotado como formato filho com suas configurações atuais
        updates.action = 'NEW';
        updates.productId = undefined;
        updates.customName = product.name;
        updates.value = calculateValueByMargin(product.cost, resolution.margin || 30);

        const unitCost = factor > 0 ? product.cost / factor : product.cost;
        updates.childFormats = [
          {
            id: existing.id,
            name: existing.name,
            fractionFactor: factor > 1 ? factor : 12,
            value: existing.value !== undefined ? existing.value : calculateValueByMargin(unitCost, resolution.margin || 30),
            barCode: existing.barCode || '',
            cost: Math.round((unitCost + Number.EPSILON) * 10000) / 10000,
            isExisting: true,
            wasAdoptedAsChild: true,
            previousAmount: existing.amount || 0,
          },
        ];
      } else {
        // Produto novo que não existia na base
        updates.action = 'NEW';
        updates.value = calculateValueByMargin(product.cost, resolution.margin || 30);

        if ((!resolution.childFormats || resolution.childFormats.length === 0) && (product.isPackage || factor > 1)) {
          if (factor > 1) {
            const cleanName = (resolution.customName || product.name || '')
              .replace(/\b\d{1,3}\s*(?:PACKS?|PKS?|PCKS?|FL|FILM|FILME|U|UN|UNID|LATAS?)\b/gi, '')
              .replace(/\b(?:CX|FD|PCT|LT|PET)\d{1,3}\b/gi, '')
              .replace(/\b(?:CX|FD|PCT|FARDO|CAIXA|PACK|FL)\b/gi, '')
              .replace(/\s+/g, ' ')
              .trim();
            const unitCost = product.cost / factor;
            updates.childFormats = [
              {
                name: cleanName || 'Unidade Avulsa',
                fractionFactor: factor,
                value: calculateValueByMargin(unitCost, resolution.margin || 30),
                barCode: '',
                isAutoSuggested: true,
              },
            ];
          }
        }
      }
    } else {
      // SIMPLE (Vender Fechado)
      updates.itemsPerUnit = 1;
      updates.childFormats = [];
      updates.value = calculateValueByMargin(product.cost, resolution.margin || 30);
      if (isExisting && !existingHasFormats) {
        updates.action = 'NEW';
        updates.productId = undefined;
      }
    }
    onChangeResolution(index, updates);
  };

  const handleValueChange = (newVal) => {
    const newMargin = calculateMarginByValue(unitCost, newVal);
    onChangeResolution(index, { value: newVal, margin: newMargin });
  };

  const handleMarginChange = (newMargin) => {
    const newVal = calculateValueByMargin(unitCost, newMargin);
    onChangeResolution(index, { margin: newMargin, value: newVal });
  };

  const requiresAttention = product.isPackage && mode === 'SIMPLE';

  return (
    <>
      <TableRow
        hover
        className={`${classes.rootRow} ${openExpand ? classes.expandedRow : ''} ${requiresAttention ? classes.attentionRow : ''}`}
        selected={isSelected}
      >
        <TableCell padding="checkbox">
          <Checkbox checked={isSelected} onChange={() => onToggleSelect(index)} />
        </TableCell>
        <TableCell padding="none">
          <IconButton size="small" onClick={() => setOpenExpand(!openExpand)}>
            {openExpand ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell style={{ minWidth: 260 }}>
          <TextField
            fullWidth
            size="small"
            value={resolution.customName !== undefined ? resolution.customName : product.name}
            onChange={(e) => onChangeResolution(index, 'customName', e.target.value)}
            InputProps={{ style: { fontSize: '0.88rem' } }}
          />
          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
            {product.barCode && product.barCode.toUpperCase() !== 'SEM GTIN' && (
              <Typography variant="caption" color="textSecondary">
                EAN: {product.barCode}
              </Typography>
            )}
            {product.isPackage && (
              <Chip
                size="small"
                label={mode === 'FRACTION_ONLY' ? `Desmembrado (x${itemsPerUnit})` : mode === 'PARENT_CHILD' ? `Caixa + ${childFormats.length} Formato(s)` : 'Embalagem Coletiva'}
                style={{
                  height: 20,
                  fontSize: '0.72rem',
                  backgroundColor: mode === 'FRACTION_ONLY' ? '#e0f2fe' : mode === 'PARENT_CHILD' ? '#eff6ff' : '#fef3c7',
                  color: mode === 'FRACTION_ONLY' ? '#0369a1' : mode === 'PARENT_CHILD' ? '#17497F' : '#b45309',
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell align="center" style={{ minWidth: 95 }}>
          <ProductUnitSelect
            variant="compact"
            size="small"
            value={currentUnit}
            onChange={(newUnit) => onChangeResolution(index, 'unit', newUnit)}
          />
          {product.unit && product.unit.toUpperCase().trim() !== currentUnit && (
            <Typography variant="caption" color="textSecondary" display="block" style={{ fontSize: '0.68rem', marginTop: 2 }}>
              NF: {product.unit}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {displayAmount}
          </Typography>
          {mode === 'FRACTION_ONLY' && (
            <Typography variant="caption" color="textSecondary" display="block">
              ({product.amount} cx × {itemsPerUnit})
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">
            {formattedCurrency(unitCost)}
          </Typography>
          {mode === 'FRACTION_ONLY' && (
            <Typography variant="caption" color="textSecondary" display="block">
              (Total cx: {formattedCurrency(product.cost)})
            </Typography>
          )}
        </TableCell>
        <TableCell style={{ width: 140 }}>
          <CurrencyTextField
            size="small"
            variant="outlined"
            decimalCharacter=","
            digitGroupSeparator="."
            currencySymbol="R$"
            outputFormat="number"
            value={saleValue}
            onChange={(e, val) => handleValueChange(val)}
          />
          <Typography variant="caption" color="textSecondary" display="block" style={{ fontSize: '0.74rem' }}>
            Margem: {Number(margin || 0).toFixed(1)}% | Lucro: {formattedCurrency(profit)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {product.analysis?.status === 'EXISTING' ? (
            <Tooltip title={`Produto já cadastrado no sistema (ID: ${product.analysis.product.id})`}>
              <Chip
                icon={<CheckCircleIcon style={{ color: '#0288d1' }} />}
                label="Existente"
                size="small"
                style={{ backgroundColor: '#e1f5fe', color: '#0288d1' }}
              />
            </Tooltip>
          ) : product.analysis?.status === 'SIMILAR' ? (
            <Tooltip title="Similaridade com produto já cadastrado">
              <Chip
                icon={<WarningIcon style={{ color: '#ed6c02' }} />}
                label="Similar"
                size="small"
                style={{ backgroundColor: '#fff4e5', color: '#ed6c02' }}
              />
            </Tooltip>
          ) : (
            <Chip
              icon={<AddBoxIcon style={{ color: '#2e7d32' }} />}
              label="Novo"
              size="small"
              style={{ backgroundColor: '#edf7ed', color: '#2e7d32' }}
            />
          )}
        </TableCell>
        <TableCell align="center">
          <Tooltip title="Excluir da importação">
            <IconButton size="small" onClick={() => onRemove(index)} style={{ color: '#ef4444' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Linha Expansível de Detalhes e Variações */}
      <TableRow className={classes.expandedRow}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={openExpand} timeout="auto" unmountOnExit>
            <Box className={classes.expandedBox}>
              <Grid container spacing={2}>
                {/* 1. MODO DE CONVERSÃO / EMBALAGEM */}
                <Grid item xs={12} md={5}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: '#334155' }}>
                    Modelo de Venda & Embalagem
                  </Typography>
                  <div className={classes.modeSegmented}>
                    <button
                      type="button"
                      className={`${classes.modeBtn} ${mode === 'FRACTION_ONLY' ? 'active' : ''}`}
                      onClick={() => handleModeChange('FRACTION_ONLY')}
                    >
                      Apenas Unidades
                    </button>
                    <button
                      type="button"
                      className={`${classes.modeBtn} ${mode === 'PARENT_CHILD' ? 'active' : ''}`}
                      onClick={() => handleModeChange('PARENT_CHILD')}
                    >
                      Caixa + Formatos Filhos
                    </button>
                    <button
                      type="button"
                      className={`${classes.modeBtn} ${mode === 'SIMPLE' ? 'active' : ''}`}
                      onClick={() => handleModeChange('SIMPLE')}
                    >
                      Vender Fechado
                    </button>
                  </div>

                  {mode === 'FRACTION_ONLY' && (
                    <Box mt={1.5}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        Transforma a caixa em unidades simples no estoque (divide custo e multiplica quantidade).
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <TextField
                          type="number"
                          size="small"
                          label="Unidades p/caixa"
                          variant="outlined"
                          style={{ width: 140 }}
                          value={itemsPerUnit}
                          onChange={(e) => {
                            const factor = Math.max(1, parseInt(e.target.value, 10) || 1);
                            onChangeResolution(index, {
                              itemsPerUnit: factor,
                              value: calculateValueByMargin(product.cost / factor, margin),
                            });
                          }}
                        />
                        <Typography variant="caption">
                          = <strong>{product.amount * itemsPerUnit} unidades</strong> a <strong>{formattedCurrency(product.cost / itemsPerUnit)}</strong> cada.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {mode === 'PARENT_CHILD' && (
                    <Box mt={1.5}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        A caixa inteira entra no estoque e formatos derivados adicionais podem ser vendidos no balcão.
                      </Typography>
                      <Box mt={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => onOpenFormatModal(
                            index,
                            product.cost,
                            resolution.customName || product.name,
                            itemsPerUnit || product.suggestedItemsPerUnit
                          )}
                        >
                          Adicionar Formato de Venda (Lata, Dose, etc.)
                        </Button>
                      </Box>

                      {childFormats.length > 0 && (
                        <Box mt={1.5}>
                          {childFormats.some((f) => f.isExisting) ? (
                            <Box p={1} mb={1} bgcolor="#f0fdf4" borderRadius={6} border="1px solid #bbf7d0">
                              <Typography variant="caption" style={{ color: '#166534', fontWeight: 600 }} display="block">
                                {childFormats.length} formato(s) de venda vinculado(s) no sistema.
                              </Typography>
                              <Typography variant="caption" style={{ color: '#15803d', fontSize: '0.73rem' }} display="block">
                                O custo das frações será recalculado automaticamente para refletir esta nova entrada da nota ({formattedCurrency(product.cost)}).
                              </Typography>
                            </Box>
                          ) : (
                            <Box p={1} mb={1} bgcolor="#eff6ff" borderRadius={6} border="1px solid #bfdbfe">
                              <Typography variant="caption" style={{ color: '#1e40af', fontWeight: 600 }} display="block">
                                Formato fracionado derivado para venda no balcão.
                              </Typography>
                            </Box>
                          )}

                          {childFormats.map((fmt, fIdx) => {
                            const calculatedCost = product.cost / (fmt.fractionFactor || 1);
                            const childValue = parseFloat(fmt.value) || 0;
                            const childProfit = childValue - calculatedCost;
                            const childMargin = childValue > 0 ? ((childProfit / childValue) * 100) : 0;
                            return (
                              <div key={fIdx} className={classes.formatChip} style={{ padding: '8px 12px', alignItems: 'center' }}>
                                <Box style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                                  <Box display="flex" alignItems="center" gap={1} style={{ minWidth: 0 }}>
                                    <Tooltip title={fmt.name || ''}>
                                      <Typography
                                        variant="subtitle2"
                                        style={{
                                          fontWeight: 600,
                                          fontSize: '0.85rem',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {fmt.name}
                                      </Typography>
                                    </Tooltip>
                                    {fmt.wasAdoptedAsChild ? (
                                      <Chip
                                        size="small"
                                        label={`Unidade #${fmt.id} Vinculada`}
                                        style={{ height: 18, fontSize: '0.68rem', backgroundColor: '#eff6ff', color: '#17497F', fontWeight: 600 }}
                                      />
                                    ) : fmt.isExisting ? (
                                      <Chip
                                        size="small"
                                        label="Existente"
                                        style={{ height: 18, fontSize: '0.68rem', backgroundColor: '#dcfce7', color: '#166534' }}
                                      />
                                    ) : null}
                                  </Box>
                                  <Typography variant="caption" color="textSecondary" display="block">
                                    Rendimento: <strong>1/{fmt.fractionFactor}</strong> • Novo Custo: <strong>{formattedCurrency(calculatedCost)}</strong> • Margem: <strong style={{ color: childProfit >= 0 ? '#16a34a' : '#dc2626' }}>{childMargin.toFixed(1)}%</strong> ({formattedCurrency(childProfit)})
                                  </Typography>
                                  {fmt.wasAdoptedAsChild && fmt.previousAmount > 0 && (
                                    <Typography variant="caption" style={{ color: '#059669', fontSize: '0.72rem', display: 'block', marginTop: 2 }}>
                                      +{fmt.previousAmount} un. em estoque serão migradas ({Math.round(((fmt.previousAmount / (fmt.fractionFactor || 1)) + Number.EPSILON) * 100) / 100} cx) para o produto pai.
                                    </Typography>
                                  )}
                                </Box>

                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box textAlign="right">
                                    <Typography variant="caption" color="textSecondary" display="block" style={{ fontSize: '0.7rem' }}>
                                      Preço de Venda
                                    </Typography>
                                    <CurrencyTextField
                                      size="small"
                                      variant="outlined"
                                      decimalCharacter=","
                                      digitGroupSeparator="."
                                      currencySymbol="R$"
                                      outputFormat="number"
                                      value={fmt.value}
                                      onChange={(e, val) => {
                                        const updated = [...childFormats];
                                        updated[fIdx] = { ...updated[fIdx], value: val };
                                        onChangeResolution(index, 'childFormats', updated);
                                      }}
                                      style={{ width: 105 }}
                                    />
                                  </Box>
                                  {!fmt.isExisting && !fmt.id && (
                                    <Tooltip title="Remover este formato">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const updated = childFormats.filter((_, i) => i !== fIdx);
                                          onChangeResolution(index, 'childFormats', updated);
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" style={{ color: '#ef4444' }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </div>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>

                {/* 2. CATEGORIA, AÇÃO & PARTICULARIDADES */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: '#334155' }}>
                    Categoria do Produto
                  </Typography>
                  <Autocomplete
                    disableClearable
                    size="small"
                    disabled={isAddAction}
                    value={selectedCategory}
                    options={categoryList}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value?.id}
                    onChange={(e, newVal) => {
                      if (newVal) {
                        onChangeResolution(index, {
                          category: newVal.id,
                          particulars: {},
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        helperText={
                          isAddAction
                            ? 'Categoria preservada do produto existente'
                            : isAdoptedAsChild
                              ? 'Categoria da nova embalagem mestre (Caixa)'
                              : undefined
                        }
                      />
                    )}
                  />

                  {product.analysis?.status !== 'NEW' && (
                    <Box mt={1.5}>
                      <Typography variant="caption" style={{ fontWeight: 600, color: '#334155' }}>
                        Ação ao Confirmar
                      </Typography>
                      <FormControl variant="outlined" size="small" fullWidth>
                        <Select
                          value={currentAction}
                          onChange={(e) => {
                            const newAction = e.target.value;
                            if (newAction === 'ADD') {
                              onChangeResolution(index, {
                                action: 'ADD',
                                category: preservedCategoryId,
                                particulars: {},
                              });
                            } else {
                              onChangeResolution(index, 'action', newAction);
                            }
                          }}
                        >
                          {product.analysis?.product && (
                            <MenuItem
                              value="ADD"
                              disabled={isAdoptedAsChild}
                            >
                              {isAdoptedAsChild
                                ? `Somar ao Estoque (Indisponível: #${product.analysis.product.id} é a fração abaixo)`
                                : `Somar ao Estoque Existente (ID: ${product.analysis.product.id})`}
                            </MenuItem>
                          )}
                          <MenuItem value="NEW">Cadastrar como Novo Produto</MenuItem>
                        </Select>
                      </FormControl>
                      {isAdoptedAsChild && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4, fontStyle: 'italic', fontSize: '0.72rem' }}>
                          O produto #{product.analysis.product.id} foi vinculado como fração de venda. A caixa da nota entrará como novo produto pai.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Controle de Particularidades para Produto Novo */}
                  {!isAddAction && hasParticulars && (
                    <Box
                      mt={1.5}
                      p={1.5}
                      style={{
                        backgroundColor: '#f8fbff',
                        border: '1px solid #d4e5f9',
                        borderRadius: 6,
                      }}
                    >
                      <Typography
                        variant="caption"
                        style={{
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          marginBottom: 8,
                          display: 'block',
                          color: '#17497F',
                        }}
                      >
                        Características da Categoria ({selectedCategory?.name})
                      </Typography>
                      <Grid container spacing={1.5}>
                        {Object.entries(categoryParticularsDef).map(([key, value]) => {
                          const currentParticulars = resolution.particulars || {};
                          const currentVal = currentParticulars[key] || '';
                          return (
                            <Grid key={key} item xs={12}>
                              <FormControl
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                error={!currentVal}
                              >
                                <InputLabel id={`label-row-part-${index}-${key}`}>
                                  {capitalize(key)} *
                                </InputLabel>
                                <Select
                                  labelId={`label-row-part-${index}-${key}`}
                                  label={`${capitalize(key)} *`}
                                  value={currentVal}
                                  onChange={(e) => {
                                    const updated = {
                                      ...currentParticulars,
                                      [key]: e.target.value,
                                    };
                                    onChangeResolution(index, 'particulars', updated);
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Selecione o(a) {key} *</em>
                                  </MenuItem>
                                  {Array.isArray(value)
                                    ? value.map((vl) => (
                                      <MenuItem key={vl} value={vl}>
                                        {vl}
                                      </MenuItem>
                                    ))
                                    : value && (
                                      <MenuItem key={value} value={value}>
                                        {value}
                                      </MenuItem>
                                    )}
                                </Select>
                                {!currentVal && (
                                  <FormHelperText error>Campo obrigatório</FormHelperText>
                                )}
                              </FormControl>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}
                </Grid>

                {/* 3. MARGEM & PREÇO DETALHADO */}
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: '#334155' }}>
                    Margem Desejada (%)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    variant="outlined"
                    value={margin}
                    onChange={(e) => handleMarginChange(parseFloat(e.target.value) || 0)}
                    InputProps={{ endAdornment: <Typography variant="caption">%</Typography> }}
                  />
                  <Box mt={1} p={1} bgcolor="#ffffff" borderRadius={4} border="1px solid #e2e8f0">
                    <Typography variant="caption" color="textSecondary" display="block">
                      Custo Unitário: <strong>{formattedCurrency(unitCost)}</strong>
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Preço Unitário: <strong>{formattedCurrency(saleValue)}</strong>
                    </Typography>
                    <Typography variant="caption" style={{ color: '#16a34a', fontWeight: 600 }} display="block">
                      Lucro Líquido: {formattedCurrency(profit)} ({Number(margin).toFixed(1)}%)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ImportRowDesktop;
